/**
 * Review-provider source resolution (TrustYou vs TripAdvisor).
 *
 * The provider is decided per hotel from data (no feature toggle), and only two sources are
 * recognised (MPH-742 — no fallback):
 *  - `TRIPADVISOR` → TripAdvisor layout (owl + bubbles + TA score).
 *  - `TRUSTYOU` → TrustYou layout and the TrustYou-backed content (gallery widget, reviews
 *    section/modal, JSONP meta-review — gated on its `opinionId`).
 *  - ANY other source (absent, empty, `BOOKING`, the Expedia family, unknown) → no reviews at
 *    all, on search and detail (gate with `hasKnownReviewSource`). Supply is expected to stop
 *    emitting them, but any straggler must not render mislabelled as TrustYou.
 *
 * The two back-end feeds are independent, one per surface, and each tags a score with
 * a single provider literal:
 *  - Search: memsearch `accommodation.rating_source` (carried on `DealCard.reviewSource`)
 *  - Detail: travel-api `opinion.source.name` (carried on `hotelOpinion.reviewSource`)
 */
export const REVIEW_SOURCE = {
  TRUSTYOU: 'TRUSTYOU',
  TRIPADVISOR: 'TRIPADVISOR',
} as const;

/** TripAdvisor layout: only an explicit `TRIPADVISOR` source. */
export const isTripAdvisorSource = (source?: string | null): boolean =>
  (source ?? '').toUpperCase() === REVIEW_SOURCE.TRIPADVISOR;

/** TrustYou layout and TrustYou-keyed content: only an explicit `TRUSTYOU` source. */
export const isTrustYouSource = (source?: string | null): boolean =>
  (source ?? '').toUpperCase() === REVIEW_SOURCE.TRUSTYOU;

/** Reviews render only for a recognised provider — an absent, empty or unknown source shows nothing. */
export const hasKnownReviewSource = (source?: string | null): boolean =>
  isTripAdvisorSource(source) || isTrustYouSource(source);

/**
 * Whether the full reviews content (detail reviews section / evo Reviews tab / summary modal)
 * can render: TripAdvisor needs a rating, TrustYou needs its opinionId.
 */
export const hasReviewsContent = (
  hotelOpinion?: { reviewSource?: string; rating?: number; opinionId?: string } | null
): boolean =>
  isTripAdvisorSource(hotelOpinion?.reviewSource)
    ? !!hotelOpinion?.rating
    : isTrustYouSource(hotelOpinion?.reviewSource) && !!hotelOpinion?.opinionId;

/** TripAdvisor shows any score; TrustYou keeps the raw-scale >= 60 floor; any other source shows nothing. */
export const isCardRatingVisible = (rating: number | undefined, source?: string | null): boolean => {
  if (!rating) return false;
  return isTripAdvisorSource(source) || (isTrustYouSource(source) && rating >= 60);
};

/**
 * Filter thresholds are page-level (no per-hotel scale): TripAdvisor values are forced when at
 * least one hotel in the result list carries a TripAdvisor source, otherwise TrustYou values show.
 */
export const hasAnyTripAdvisorSource = (sources: ReadonlyArray<string | null | undefined>): boolean =>
  sources.some(isTripAdvisorSource);
